import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { adminAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

const COLLECTION_OPTIONS = [
  { key: 'orders', label: 'Orders', warn: 'Removes ALL customer orders & order history.' },
  { key: 'carts', label: 'Active Carts', warn: 'Empties every customer\'s saved cart.' },
  { key: 'products', label: 'Products', warn: 'Removes every product from the catalogue.' },
  { key: 'suppliers', label: 'Suppliers', warn: 'Removes every vendor.' },
  { key: 'categories', label: 'Sub-Categories', warn: 'Removes every sub-category.' },
  { key: 'main_categories', label: 'Main Categories', warn: 'Removes Food / Health / Body & Beauty / Home & Lifestyle (re-seeded on next restart).' },
  { key: 'collection_points', label: 'Collection Points', warn: 'Removes every collection point.' },
  { key: 'users', label: 'Customers (excl. admins)', warn: 'Removes every non-admin user. Admin accounts are preserved.' },
];

export const DangerZone = () => {
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const loadCounts = () => {
    adminAPI.resetCounts()
      .then(r => setCounts(r.data || {}))
      .catch(() => {});
  };

  useEffect(() => { loadCounts(); }, []);

  const toggle = (key) => setSelected(s => ({ ...s, [key]: !s[key] }));
  const chosen = Object.keys(selected).filter(k => selected[k]);

  const handleConfirm = async () => {
    if (confirmText.trim() !== 'RESET') {
      toast.error('Type RESET to confirm');
      return;
    }
    setBusy(true);
    try {
      const res = await adminAPI.resetCollections(chosen, 'RESET');
      const total = Object.values(res.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Reset complete — ${total} document${total === 1 ? '' : 's'} removed`);
      setConfirmOpen(false);
      setSelected({});
      setConfirmText('');
      loadCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="danger-zone" className="p-4 sm:p-6 rounded-2xl bg-card border-2 border-red-700/40">
      <div className="flex items-center gap-3 mb-1">
        <AlertTriangle className="h-5 w-5 text-red-400" strokeWidth={2} />
        <h2 className="font-heading text-xl text-red-400">Danger Zone</h2>
      </div>
      <p className="font-body text-xs text-foreground/50 mb-5">
        Wipe selected data permanently. Use with care — this cannot be undone.
        Admin user accounts are always preserved.
      </p>

      <div className="space-y-2 mb-5">
        {COLLECTION_OPTIONS.map(opt => (
          <label
            key={opt.key}
            data-testid={`reset-option-${opt.key}`}
            className="flex items-start gap-3 p-3 rounded-xl border border-border hover:border-red-700/40 cursor-pointer transition-colors"
          >
            <Checkbox
              checked={!!selected[opt.key]}
              onCheckedChange={() => toggle(opt.key)}
              className="mt-0.5 border-border data-[state=checked]:bg-red-700 data-[state=checked]:border-red-700"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-body text-sm font-semibold text-foreground">{opt.label}</span>
                <span className="font-body text-xs text-foreground/40 flex-shrink-0">
                  {counts[opt.key] ?? '–'} row{counts[opt.key] === 1 ? '' : 's'}
                </span>
              </div>
              <p className="font-body text-[11px] text-foreground/40 mt-0.5">{opt.warn}</p>
            </div>
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button
          onClick={loadCounts}
          variant="ghost"
          size="sm"
          className="rounded-full font-body text-xs gap-1.5 text-foreground/50 hover:text-foreground"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={2} /> Refresh counts
        </Button>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={chosen.length === 0}
          data-testid="open-reset-confirm"
          className="rounded-full bg-red-700 text-white hover:bg-red-800 disabled:opacity-40 font-body text-sm font-semibold gap-2"
        >
          <AlertTriangle className="h-4 w-4" strokeWidth={2} />
          Reset {chosen.length || ''} {chosen.length === 1 ? 'collection' : 'collections'}
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!busy) { setConfirmOpen(o); if (!o) setConfirmText(''); } }}>
        <DialogContent className="bg-background rounded-2xl max-w-md" data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-400 inline-flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" strokeWidth={2} /> Final confirmation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="font-body text-sm text-foreground/80">
              You are about to permanently delete data from:
            </p>
            <ul className="font-body text-sm text-red-300 list-disc pl-5 space-y-0.5">
              {chosen.map(k => {
                const opt = COLLECTION_OPTIONS.find(o => o.key === k);
                return <li key={k}>{opt?.label} <span className="text-foreground/40">({counts[k] ?? 0} rows)</span></li>;
              })}
            </ul>
            <p className="font-body text-xs text-foreground/50">
              Type <strong className="text-red-400 tracking-wider">RESET</strong> in the box below to confirm.
            </p>
            <Input
              data-testid="reset-confirm-input"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="Type RESET"
              className="rounded-xl bg-card border-red-700/40 focus:border-red-500 font-body text-sm"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              className="rounded-full border-border font-body text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={busy || confirmText.trim() !== 'RESET'}
              data-testid="reset-confirm-btn"
              className="rounded-full bg-red-700 text-white hover:bg-red-800 disabled:opacity-40 font-body text-sm font-semibold gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" strokeWidth={2} />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
